import { AccountAddress } from '../../../../shared/types/modules/account';
import {
    readOrbitCitizenString,
    readOrbitString,
    resolveOrbitPreferredContact,
} from '../../../../shared/utils/orbitCaseDetail.util';
import {
    OrbitDocumentTypeEnum,
    OrbitPersonTypeEnum,
} from '../constants/citizenCatalogs';
import { OrbitChannelFormValues } from '../channelForm/formData';
import { OrbitCitizenInfoValues } from '../citizenInfo/formData';
import { OrbitPqrInfoValues } from '../pqrInfo/formData';
import { OrbitPrivacyConsentValues } from '../privacyConsent/formData';
import { OrbitSpecialConditionValues } from '../specialCondition/formData';

export type OrbitRadicationFormValues = {
    channel: OrbitChannelFormValues;
    citizen: OrbitCitizenInfoValues;
    address: AccountAddress;
    specialCondition: OrbitSpecialConditionValues;
    pqrInfo: OrbitPqrInfoValues;
    privacyConsent: OrbitPrivacyConsentValues;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : undefined;

const splitCitizenName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
        return { name: '', lastName: '' };
    }
    return {
        name: parts[0],
        lastName: parts.slice(1).join(' '),
    };
};

const emptyAddressFields = (): Omit<AccountAddress, 'address'> => ({
    city: '',
    type1: '',
    type2: '',
    number1: '',
    letter1: '',
    letter2: '',
    quadrant1: '',
    number2: '',
    letter3: '',
    letter4: '',
    quadrant2: '',
    number3: '',
    quadrant3: '',
    complement: '',
});

const resolvePersonTypeLabel = (
    orbitCase: Record<string, unknown>,
    customFields?: Record<string, unknown>
): string => {
    const fromCustomFields = readOrbitString(customFields ?? {}, [
        'person_type',
    ]);

    if (fromCustomFields) return fromCustomFields;

    const documentType = readOrbitCitizenString(orbitCase, [
        'document_type',
    ]).toUpperCase();

    if (documentType === OrbitDocumentTypeEnum.NIT) {
        return OrbitPersonTypeEnum.Juridica;
    }

    return documentType ? OrbitPersonTypeEnum.Natural : '';
};

export const mapOrbitCaseToRadicationValues = (
    orbitCase: Record<string, unknown>
): OrbitRadicationFormValues => {
    const customFields = asRecord(orbitCase.custom_fields);
    const metadata = asRecord(orbitCase.metadata);

    const fullName = readOrbitCitizenString(orbitCase, ['full_name']);
    const { name, lastName } = splitCitizenName(fullName);
    const typeDocument = readOrbitCitizenString(orbitCase, ['document_type']);

    return {
        channel: {
            orbitChannel: readOrbitString(orbitCase, [
                'channel_label',
            ]) as OrbitChannelFormValues['orbitChannel'],
            sfcAdmission: readOrbitString(customFields ?? {}, [
                'admision',
                'admission',
            ]),
            correspondenceFiling: readOrbitString(customFields ?? {}, [
                'radicado_correspondencia',
                'correspondence_filing',
            ]),
        },
        citizen: {
            personType: resolvePersonTypeLabel(orbitCase, customFields),
            typeDocument,
            document: readOrbitCitizenString(orbitCase, ['document']),
            name,
            lastName,
            email: readOrbitCitizenString(orbitCase, ['email']),
            alternateEmail: readOrbitCitizenString(orbitCase, [
                'alternative_email',
            ]),
            cellPhone: readOrbitCitizenString(orbitCase, [
                'mobile_phone',
                'phone',
            ]),
            phone: '',
            gender: readOrbitString(customFields ?? {}, ['gender']),
            isLGBTIQ: Boolean(customFields?.is_lgbtiq ?? false),
        },
        address: {
            address: readOrbitCitizenString(orbitCase, ['address']),
            ...emptyAddressFields(),
        },
        specialCondition: {
            hasSpecialCondition: Boolean(
                customFields?.has_special_condition ?? false
            ),
            specialCondition: readOrbitString(customFields ?? {}, [
                'special_condition',
            ]),
            observations: readOrbitString(customFields ?? {}, [
                'special_condition_observations',
            ]),
        },
        pqrInfo: {
            country: 'Colombia',
            department: readOrbitString(orbitCase, ['event_department']),
            city: readOrbitString(orbitCase, ['event_city']),
            caseType: readOrbitString(orbitCase, [
                'case_type_label',
                'case_type_name',
                'case_type',
            ]),
            detail: readOrbitString(orbitCase, ['description']),
            preferredContactMethod: resolveOrbitPreferredContact(orbitCase),
            attachments: [],
        },
        privacyConsent: {
            acceptTerms: Boolean(
                orbitCase.habeas_data_accepted ?? metadata?.accept_terms ?? true
            ),
        },
    };
};
